import Vehicle from '../interfaces/Vehicle'

export interface VehiclePublicationStatus {
  isPublishedToAny: boolean
  isPublishedToYapo: boolean
  isPublishedToChileAutos: boolean
  isPublishedToMercadoLibre: boolean
  publishedPlatforms: string[]
  /*
  Hay una baja aceptada y todavía sin confirmar. Es información aparte de
  `isPublishedToYapo` a propósito: mientras se confirma, el vehículo SIGUE
  publicado —el aviso está a la vista de los compradores— así que las
  validaciones que preguntan "¿está publicado?" tienen que seguir diciendo sí.
  Lo que cambia es lo que se le muestra al usuario, que sí pidió retirarlo.
  */
  isUnpublishPendingToYapo: boolean
}

/**
 * Detects if a vehicle is published to any platform and returns detailed status
 * @param vehicle - Vehicle object to check
 * @returns VehiclePublicationStatus object with publication details
 */
export const getVehiclePublicationStatus = (vehicle: Vehicle): VehiclePublicationStatus => {
  const isPublishedToYapo = !!vehicle.external_yapo?.publishedAt
  const isPublishedToChileAutos = !!vehicle.external_chileautos?.publishedAt
  const isPublishedToMercadoLibre = !!vehicle.external_meli?.publishedAt
  const isUnpublishPendingToYapo = vehicle.unpublishStatus?.yapo?.state === 'pending'

  const publishedPlatforms: string[] = []

  if (isPublishedToYapo) publishedPlatforms.push('Yapo')

  if (isPublishedToChileAutos) publishedPlatforms.push('ChileAutos')

  if (isPublishedToMercadoLibre) publishedPlatforms.push('MercadoLibre')

  const isPublishedToAny = publishedPlatforms.length > 0

  return {
    isPublishedToAny,
    isPublishedToYapo,
    isPublishedToChileAutos,
    isPublishedToMercadoLibre,
    publishedPlatforms,
    isUnpublishPendingToYapo,
  }
}

/**
 * Simple helper to check if vehicle is published to any platform
 * @param vehicle - Vehicle object to check
 * @returns boolean indicating if vehicle is published to any platform
 */
export const isVehiclePublishedToAnyPlatform = (vehicle: Vehicle): boolean => {
  return getVehiclePublicationStatus(vehicle).isPublishedToAny
}

/**
 * Returns a human-readable string of platforms where the vehicle is published
 * @param vehicle - Vehicle object to check
 * @returns string with platform names
 */
export const getPublishedPlatformsString = (vehicle: Vehicle): string => {
  const status = getVehiclePublicationStatus(vehicle)

  if (!status.isPublishedToAny) {
    return 'No publicado'
  }

  return status.publishedPlatforms.join(', ')
}
